import React, { useState, useEffect } from 'react';
import fr from 'facet-rest';
import moment from 'moment';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import QueryBuilderIcon from '@material-ui/icons/QueryBuilder';
import EventIcon from '@material-ui/icons/Event';
import { Tooltip, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { noop } from 'Helpers/PropTypes';
import Button from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Button';
import ScheduleSendDatePicker from 'Components/Modals/ScheduleSendDatePicker';
import ConfirmationDialog from 'Components/UI/ConfirmationDialog';
import ScheduleSendReview from 'Components/Modals/ScheduleSendReview';
import { shortDateAtTime } from 'Consts/DateFormats';
import { useSnackbarContext } from 'Context/SnackbarContext';

const styles = theme => ({
  root: {
    display: 'flex',
    backgroundColor: theme.palette.common.white,
    marginBottom: theme.spacing(0.5),
    boxShadow: '0px 3px 15px #0000000B',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '3px 0px',
  },
  itemContainer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'none',
      color: 'inherit',
      backgroundColor: theme.palette.facet.lightGray,
    },
  },
  itemAccent: {
    width: '5px',
    height: '100%',
    margin: '3px 8px 3px 3px',
    backgroundColor: theme.palette.facet.accentBlue,
  },
  itemText: {
    color: theme.palette.facet.primaryGray,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dateText: {
    color: theme.palette.facet.accentBlue2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dateTextIcon: {
    marginRight: theme.spacing(0.5),
  },
  reminderButton: {
    padding: theme.spacing(0, 1),
  },
  feedback: {
    display: 'flex',
    marginLeft: theme.spacing(0.5),
    alignItems: 'center',
  },
  button: {
    marginRight: theme.spacing(0.5),
  },
  feedbackButton: {
    padding: theme.spacing(0.5),
    height: 30,
    width: 30,
  },
  complete: {
    color: theme.palette.facet.accentGreen,
  },
  cancel: {
    color: theme.palette.facet.red,
  },
  reminderText: {
    display: 'flex',
    flexDirection: 'column',
  },
  reviewStatus: {
    alignSelf: 'center',
    marginRight: theme.spacing(1),
  },
  loader: {
    height: '90px',
    width: '100%',
    marginBottom: theme.spacing(1),
  },
});

const MeetingAgendaCard = ({
  classes,
  meetingAgendaResource,
  refreshCollection,
  allowReschedule,
  rootNodeProps,
  logEventSuccess,
  logEventFailure,
}) => {
  const { displaySuccess, displayErrorMessage } = useSnackbarContext();
  const [isLoading, setLoading] = useState(true);
  const [meetingResource, setMeetingResource] = useState(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [reviewScheduleSendModalOpen, setReviewScheduleSendModalOpen] = useState(false);

  const [scheduledSendDatePickerIsOpen, setScheduledSendDatePickerIsOpen] = useState(false);

  const deleteMeetingAgenda = async () => {
    try {
      await meetingAgendaResource.delete();
      logEventSuccess('Deleted a Scheduled Meeting Agenda', { meetingAgendaId: meetingAgendaResource.id() });
      displaySuccess('Deleted the Scheduled Email');
    } catch (err) {
      logEventFailure('Failed to deleted a Scheduled Meeting Agenda', { meetingAgendaId: meetingAgendaResource.id() });
      displayErrorMessage(err, err.errors);
    }
    refreshCollection();
  };

  const loadMeeting = async () => {
    try {
      setLoading(true);
      if (!meetingAgendaResource?.relationship(fr.relationships.meeting.name)) {
        return;
      }
      const [meetingResponse] = await meetingAgendaResource.relationship(fr.relationships.meeting.name).link('related').get();
      setMeetingResource(meetingResponse);
    } catch (err) {
      logEventFailure('Failed to deleted fetch Scheduled Meeting Agenda', { meetingAgendaId: meetingAgendaResource.id() });
      displayErrorMessage(err, err.errors);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeeting();
  }, []);

  const sendAtMoment = moment(meetingAgendaResource.attr('sendAt'));
  const meetingDateTime = moment(meetingResource?.attr('startTime'));

  return isLoading ? (
    <Skeleton
      variant="rect"
      className={classes.loader}
    />
  ) : (
    <div
      className={classes.root}
      {...rootNodeProps}
    >
      <div
        className={classes.itemContainer}
        tabIndex={0}
        role="button"
        onKeyDown={() => { }}
        onClick={() => {
          setReviewScheduleSendModalOpen(true);
        }}
      >
        <div className={classes.itemAccent} />
        <div className={classes.reminderText}>
          <Typography
            variant="h6"
            className={classes.itemText}
          >
            {meetingAgendaResource.attr('householdName')}
          </Typography>
          <Typography
            variant="h6"
            className={classes.itemText}
          >
            {`Subject: ${meetingAgendaResource.attr('subject')}`}
          </Typography>
          <Typography
            variant="h6"
            className={classes.itemText}
          >
            {`Household ID: ${meetingAgendaResource.attr('householdId')}`}
          </Typography>
          <Typography
            variant="subtitle2"
            className={classes.dateText}
          >
            <QueryBuilderIcon
              fontSize="small"
              className={classes.dateTextIcon}
            />
            {`Scheduled to send on: ${sendAtMoment.format(shortDateAtTime)}`}
          </Typography>
          <Typography variant="subtitle2" className={classes.dateText}>
            <EventIcon
              fontSize="small"
              className={classes.dateTextIcon}
            />
            {`For meeting on: ${meetingDateTime.format(shortDateAtTime)}`}
          </Typography>
        </div>
      </div>
      <div className={classes.feedback}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {allowReschedule ? (
          <>
            <Button
              color="secondary"
              onClick={() => {
                setScheduledSendDatePickerIsOpen(true);
              }}
              className={classes.button}
            >
              Reschedule
            </Button>
            <ScheduleSendDatePicker
              isOpen={scheduledSendDatePickerIsOpen}
              closeModal={() => {
                setScheduledSendDatePickerIsOpen(false);
              }}
              meetingAgendaResource={meetingAgendaResource}
              onSubmit={() => {
                logEventSuccess('Rescheduled a scheduled email', { meetingAgendaId: meetingAgendaResource.id() });
                displaySuccess('Email successfully rescheduled');
                refreshCollection();
              }}
            />
          </>
        ) : (
          meetingAgendaResource.attr('isReviewed') === 'true' ? (
            <Typography color="secondary" className={classes.reviewStatus}>Reviewed</Typography>
          ) : (
            <Tooltip title="Review Scheduled Send" placement="top" arrow>
              <IconButton
                className={classNames(classes.complete, classes.feedbackButton)}
                onClick={() => {
                  setReviewScheduleSendModalOpen(true);
                }}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )
        )}
        <Tooltip title="Delete Scheduled Send" placement="top" arrow>
          <IconButton
            className={classNames(classes.cancel, classes.feedbackButton)}
            onClick={() => {
              setShowConfirmationDialog(true);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {showConfirmationDialog && (
          <ConfirmationDialog
            onConfirm={async () => {
              await deleteMeetingAgenda();
              setShowConfirmationDialog(false);
            }}
            onCancel={() => {
              setShowConfirmationDialog(false);
            }}
            confirmLabel="Confirm"
            message={`You are about to delete the Scheduled Email to ${meetingAgendaResource.attr('householdName')} with subject: ${meetingAgendaResource.attr('subject')}`}
          />
        )}
        {reviewScheduleSendModalOpen && (
          <ScheduleSendReview
            meetingAgendaResource={meetingAgendaResource}
            isOpen={reviewScheduleSendModalOpen}
            closeModal={() => setReviewScheduleSendModalOpen(false)}
            refreshResources={refreshCollection}
          />
        )}
      </div>
    </div>
  );
};

MeetingAgendaCard.defaultProps = {
  refreshCollection: noop,
  allowReschedule: false,
};

MeetingAgendaCard.propTypes = {
  classes: PropTypes.object.isRequired,
  meetingAgendaResource: PropTypes.object.isRequired,
  refreshCollection: PropTypes.func,
  allowReschedule: PropTypes.bool,
  rootNodeProps: PropTypes.object.isRequired,
  logEventSuccess: PropTypes.func.isRequired,
  logEventFailure: PropTypes.func.isRequired,
};

export default withStyles(styles)(MeetingAgendaCard);
